import {
  useBrowseProductEntitlementApi,
  useBrowseSubscriptionPlanApi,
} from '@aibees/shared/api';
import { CatalogProduct, Plan, PlanInterval } from '@aibees/shared/definition';
import { useEffect, useMemo } from 'react';

export interface PlanCardEntry {
  product: CatalogProduct;
  plans: Plan[];
  isFree: boolean;
}

function sortProducts(products: CatalogProduct[]): CatalogProduct[] {
  return [...products].sort(
    (a, b) =>
      (Number(a.metadata?.order) || 0) - (Number(b.metadata?.order) || 0)
  );
}

function getProductPlans(
  productId: string,
  plans: Plan[] | undefined,
  freePlan: Plan | undefined
): Plan[] {
  const paid = plans?.filter((p) => p.product?.id === productId) ?? [];
  const free = freePlan && freePlan.product?.id === productId ? [freePlan] : [];
  return [...free, ...paid.filter((p) => !!p.amount)];
}

export function usePlanList(yearly: boolean) {
  const interval = yearly ? PlanInterval.YEAR : PlanInterval.MONTH;
  const {
    data: plans,
    refetch,
    isLoading: plansLoading,
  } = useBrowseSubscriptionPlanApi(interval);
  const { data: monthlyPlans } = useBrowseSubscriptionPlanApi(
    PlanInterval.MONTH
  );
  const { data: entitlementData, isLoading: productsLoading } =
    useBrowseProductEntitlementApi();

  const freePlan = monthlyPlans?.find((plan) => plan.amount === 0);

  useEffect(() => {
    refetch();
  }, [yearly]);

  const productCards = useMemo<PlanCardEntry[]>(() => {
    const products = sortProducts(
      (entitlementData?.products ?? []).filter(
        (p) => p.active && p.metadata?.is_public === 'true'
      )
    );

    return products.map((product) => {
      const productPlans = getProductPlans(product.id, plans, freePlan);
      const isFree =
        productPlans.length > 0 && productPlans.every((p) => !p.amount);
      return { product, plans: productPlans, isFree };
    });
  }, [entitlementData, plans, freePlan]);

  return {
    plans,
    freePlan,
    productCards,
    isLoading: plansLoading || productsLoading,
  };
}
